/**
 * Utility Functions
 * Helper functions for common tasks
 */

import { isValidEmail } from './EmailValidation.js';
import { isClosedStage } from '../constants/Stages.js';

/**
 * Generate unique ID
 */
export function generateId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function pad2(n) {
    return String(n).padStart(2, '0');
}

function isRealCalendarDate(year, month, day) {
    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    if (!y || m < 1 || m > 12 || d < 1 || d > 31) return false;
    const date = new Date(`${year}-${month}-${day}T00:00:00`);
    return date.getFullYear() === y && date.getMonth() + 1 === m && date.getDate() === d;
}

/**
 * Format a Date as YYYY-MM-DD in local timezone (avoids UTC shift from toISOString).
 */
export function formatDateLocal(date) {
    if (typeof date === 'string') {
        return toIsoDateString(date) || date.slice(0, 10);
    }
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Normalize any date-like value to YYYY-MM-DD for storage, hidden inputs, and compares.
 * Date-only strings are not timezone-shifted. ISO datetimes use the local calendar date.
 */
export function toIsoDateString(value) {
    if (!value && value !== 0) return '';
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? '' : formatDateLocal(value);
    }

    const str = String(value).trim();
    if (!str) return '';

    if (/T\d{2}:\d{2}/.test(str) || /Z$/.test(str)) {
        const parsed = new Date(str);
        return Number.isNaN(parsed.getTime()) ? '' : formatDateLocal(parsed);
    }

    const datePart = str.split('T')[0];
    const iso = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) {
        const [, year, month, day] = iso;
        return isRealCalendarDate(year, month, day) ? `${year}-${month}-${day}` : '';
    }

    const mdy = datePart.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (mdy) {
        const month = pad2(mdy[1]);
        const day = pad2(mdy[2]);
        const year = mdy[3];
        return isRealCalendarDate(year, month, day) ? `${year}-${month}-${day}` : '';
    }

    const parsed = new Date(str);
    return Number.isNaN(parsed.getTime()) ? '' : formatDateLocal(parsed);
}

/**
 * Shift a YYYY-MM-DD date string by a number of years.
 */
export function shiftDateString(dateStr, years) {
    const iso = toIsoDateString(dateStr);
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00`);
    d.setFullYear(d.getFullYear() + years);
    return formatDateLocal(d);
}

/**
 * Format date to ISO string (YYYY-MM-DD)
 */
export function formatDateISO(date) {
    return toIsoDateString(date);
}

/**
 * Visible date text for inputs (MM-DD-YYYY). Empty string when the value is missing.
 */
export function toDisplayDate(value) {
    const iso = toIsoDateString(value);
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    return `${month}-${day}-${year}`;
}

/**
 * Format date for display (e.g., "01-15-2026")
 */
export function formatDateDisplay(dateString) {
    if (!dateString) return '—';
    return toDisplayDate(dateString) || '—';
}

/**
 * Format currency
 */
export function formatCurrency(value, decimals = 2) {
    if (typeof value !== 'number') {
        value = parseFloat(value) || 0;
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals
    }).format(value);
}

/**
 * Format short currency (e.g., "$1.2M", "$500K")
 */
export function formatCurrencyShort(value) {
    if (typeof value !== 'number') {
        value = parseFloat(value) || 0;
    }

    if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1) + 'M';
    }
    if (value >= 1000) {
        return '$' + (value / 1000).toFixed(1) + 'K';
    }
    return '$' + formatNumber(value, 0);
}

/**
 * Format percentage
 */
export function formatPercentage(value, decimals = 1) {
    if (typeof value !== 'number') {
        value = parseFloat(value) || 0;
    }
    return (Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)) + '%';
}

/**
 * Format number with thousand separators
 */
export function formatNumber(value, decimals = 0) {
    if (typeof value !== 'number') {
        value = parseFloat(value) || 0;
    }
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value);
}

/**
 * Format a number with comma separators and up to N decimals (no forced trailing zeros).
 */
export function formatAmount(value, maxDecimals = 2) {
    if (typeof value !== 'number') {
        value = parseFloat(value) || 0;
    }
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxDecimals
    }).format(value);
}

/**
 * Round to 2 decimals and strip trailing .00 (for type=number inputs).
 */
export function stripTrailingZeros(value) {
    const num = typeof value === 'number' ? value : parseFloat(value) || 0;
    return num.toFixed(2).replace(/\.00$/, '');
}

/**
 * Format a raw amount string with thousand separators as the user types.
 */
export function formatAmountInput(raw, maxDecimals = 2) {
    if (raw == null || raw === '') return '';
    const cleaned = String(raw).replace(/[^\d.]/g, '');
    const dotIdx = cleaned.indexOf('.');
    let intPart = dotIdx >= 0 ? cleaned.slice(0, dotIdx) : cleaned;
    let decPart = dotIdx >= 0 ? cleaned.slice(dotIdx + 1).replace(/\./g, '').slice(0, maxDecimals) : '';
    intPart = intPart.replace(/^0+(?=\d)/, '') || (decPart || cleaned.includes('.') ? '0' : '');
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (dotIdx >= 0 || cleaned.endsWith('.')) {
        return decPart !== '' ? `${withCommas}.${decPart}` : `${withCommas}.`;
    }
    return withCommas;
}

/**
 * Parse a comma-formatted amount string to a number.
 */
export function parseFormattedNumber(value) {
    if (value == null || value === '') return 0;
    const normalized = String(value).replace(/,/g, '').replace(/\.$/, '');
    return parseFloat(normalized) || 0;
}

/**
 * Get month name from date
 */
export function getMonthName(dateString) {
    const date = new Date(dateString + 'T00:00:00');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${m}-${date.getFullYear()}`;
}

/**
 * Get quarter from date
 */
export function getQuarter(dateString) {
    const date = new Date(dateString + 'T00:00:00');
    const month = date.getMonth();
    const quarter = Math.floor(month / 3) + 1;
    const year = date.getFullYear();
    return `Q${quarter} ${year}`;
}

/**
 * Get week number from date
 */
export function getWeekNumber(dateString) {
    const date = new Date(dateString + 'T00:00:00');
    const firstDay = new Date(date.getFullYear(), 0, 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayMs = 24 * 60 * 60 * 1000;
    const weekNum = Math.ceil((lastDay - firstDay) / dayMs / 7) + 1;
    return weekNum;
}

/**
 * Calculate days between two dates
 */
export function daysBetween(startDate, endDate) {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    const dayMs = 24 * 60 * 60 * 1000;
    return Math.ceil((end - start) / dayMs);
}

/**
 * Check if date is today
 */
export function isToday(dateString) {
    const date = new Date(dateString + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() === today.getTime();
}

/**
 * Check if date is in past
 */
export function isPast(dateString) {
    const date = new Date(dateString + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
}

/**
 * Urgency for expected close dates: overdue (past), urgent (within 30 days), or none.
 * Closed Won / Closed Lost deals never get urgency styling.
 */
export function getExpectedCloseDateUrgency(dateString, stage) {
    if (!dateString || isClosedStage(stage)) return '';
    const date = new Date(dateString + 'T00:00:00');
    if (Number.isNaN(date.getTime())) return '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return 'overdue';
    const limit = new Date(today);
    limit.setDate(limit.getDate() + 30);
    if (date <= limit) return 'urgent';
    return '';
}

export function formatExpectedCloseDateHtml(dateString, stage) {
    if (!dateString) return '—';
    const text = formatDateDisplay(dateString);
    const urgency = getExpectedCloseDateUrgency(dateString, stage);
    if (!urgency) return text;
    return `<span class="close-date-${urgency}">${text}</span>`;
}

/**
 * Check if date is in future
 */
export function isFuture(dateString) {
    const date = new Date(dateString + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
}

/**
 * Add days to date
 */
export function addDays(dateString, days) {
    const date = new Date(dateString + 'T00:00:00');
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
}

/**
 * Format duration in days to human readable format
 */
export function formatDuration(days) {
    if (days < 7) {
        return `${days} day${days !== 1 ? 's' : ''}`;
    }
    if (days < 30) {
        const weeks = Math.floor(days / 7);
        return `${weeks} week${weeks !== 1 ? 's' : ''}`;
    }
    if (days < 365) {
        const months = Math.floor(days / 30);
        return `${months} month${months !== 1 ? 's' : ''}`;
    }
    const years = Math.floor(days / 365);
    return `${years} year${years !== 1 ? 's' : ''}`;
}

/**
 * Debounce function
 */
export function debounce(func, delay) {
    let timeoutId;
    return function (...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
}

/**
 * Throttle function
 */
export function throttle(func, limit) {
    let inThrottle;
    return function (...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
}

/**
 * Deep clone object
 */
export function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

/**
 * Merge objects
 */
export function mergeObjects(target, source) {
    return { ...target, ...source };
}

/**
 * Validate email
 */
export function validateEmail(email) {
    return isValidEmail(email);
}

/**
 * Truncate string
 */
export function truncateString(str, length, suffix = '...') {
    if (str.length <= length) {
        return str;
    }
    return str.substr(0, length - suffix.length) + suffix;
}

/**
 * Capitalize string
 */
export function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Convert snake_case to camelCase
 */
export function toCamelCase(str) {
    return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
}

/**
 * Convert camelCase to snake_case
 */
export function toSnakeCase(str) {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Format date for input fields (YYYY-MM-DD format)
 */
export function formatDateInput(value) {
    return toIsoDateString(value);
}

/**
 * Parse date from input field value.
 * Accepts YYYY-MM-DD, MM-DD-YYYY (date picker display), or ISO datetime.
 */
export function parseDateInput(value) {
    if (!value || typeof value !== 'string') return '';
    return toIsoDateString(value);
}
